import {gql, type TypedDocumentNode} from '@apollo/client';
import {useQuery} from '@apollo/client/react';
import {useHeaderHeight} from '@react-navigation/elements';
import type {StackScreenProps} from '@react-navigation/stack';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';
import {Platform} from 'react-native';

import rootPackage from '../../../package.json';
import {expr} from '../../mobx-toolbox';
import {useRoot} from '../../Root';
import type {RootStackParamList} from '../../RootStack/RootStackParamList';
import type {ThemeId, ThemeItems} from '../../screens/PromptSettingsScreen';
import {PromptSettingsScreen} from '../../screens/PromptSettingsScreen';
import classicTheme from '../../styling/Theme/classicTheme';
import darkTheme from '../../styling/Theme/darkTheme';
import lightTheme from '../../styling/Theme/lightTheme';
import {
  defaultThemeRecord,
  isLightThemeKind,
  ThemeKind,
} from '../../ThemeRecord';
import type {
  GetCardsQuery,
  GetCardsQueryVariables,
} from './PromptSettingsBinding.generated';

export type PromptSettingsBindingProps = StackScreenProps<
  RootStackParamList,
  'PromptSettings'
>;

export default observer(function PromptSettingsBinding(
  props: PromptSettingsBindingProps,
) {
  const {navigation} = props;
  const onSetupPress = useCallback(() => {
    navigation.navigate('PromptSetup');
  }, [navigation]);
  const {data: cardsData} = useQuery(GET_CARDS);
  const onCardPress = useCallback(
    (id: number) => {
      navigation.navigate('ShowMeetingCard', {id: `${id}`});
    },
    [navigation],
  );
  const headerHeight = useHeaderHeight();
  const root = useRoot();
  const getToday = useCallback(
    () => root.currentTime.wrapped.format('D MMMM YYYY').toLowerCase(),
    [root],
  );
  const getDoesObeySystem = useCallback(
    () => root.themeStorage.current?.mustObeySystem,
    [root],
  );
  const toggleSystemObedience = useCallback(() => {
    root.themeStorage.set((_ = defaultThemeRecord) => ({
      ..._,
      mustObeySystem: !_.mustObeySystem,
    }));
  }, [root]);
  const $themeItems = useCallback(
    () =>
      expr(() => {
        const {mustObeySystem, fixed, light, dark} =
          root.themeStorage.current ?? defaultThemeRecord;
        const currentLightTheme = mustObeySystem ? light : fixed;
        const currentDarkTheme = mustObeySystem ? dark : fixed;
        const classicSelected = currentDarkTheme === ThemeKind.Classic;
        const darkSelected = currentDarkTheme === ThemeKind.Dark;
        const lightSelected = currentLightTheme === ThemeKind.Light;
        return [
          {
            id: ThemeKind.Classic as ThemeId,
            theme: classicTheme,
            title: 'Классическая',
            selected: classicSelected,
          },
          {
            id: ThemeKind.Dark as ThemeId,
            theme: darkTheme,
            title: 'Тёмная',
            selected: darkSelected,
          },
          {
            id: ThemeKind.Light as ThemeId,
            theme: lightTheme,
            title: 'Светлая',
            selected: lightSelected,
          },
        ] satisfies ThemeItems;
      }),
    [root],
  );
  const onThemeItemPress = useCallback(
    (id: ThemeId) => {
      const kind = id as ThemeKind;
      root.themeStorage.set((_ = defaultThemeRecord) => {
        if (_.mustObeySystem) {
          if (isLightThemeKind(kind)) {
            return {..._, light: kind};
          }
          return {..._, dark: kind};
        }
        return {..._, fixed: kind};
      });
    },
    [root],
  );
  return (
    <PromptSettingsScreen
      getToday={getToday}
      getDoesObeySystem={getDoesObeySystem}
      toggleSystemObedience={toggleSystemObedience}
      $themeItems={$themeItems}
      onThemeItemPress={onThemeItemPress}
      onSetupPress={onSetupPress}
      getCards={cardsData?.CardSet?.cards}
      onCardPress={onCardPress}
      platformName={translatePlatformName(Platform.OS)}
      appVersion={rootPackage.version}
      compensateHeaderHeight={headerHeight}
    />
  );
});

function translatePlatformName(_: typeof Platform.OS) {
  switch (_) {
    case 'android':
      return 'Android';
    case 'ios':
      return 'iOS';
    case 'web':
      return 'Web';
    case 'macos':
      return 'macOS';
    case 'windows':
      return 'Windows';
    default:
      return 'Misc';
  }
}

const GET_CARDS: TypedDocumentNode<GetCardsQuery, GetCardsQueryVariables> = gql`
  query GetCards {
    CardSet {
      cards {
        id
        title
        backgroundColor
        textColor
        borderColor
      }
    }
  }
`;
